// (c) 2026 Owl Consultations. All rights reserved.
// Proprietary and confidential — licensed use only.

// D-COST-6 — the tables of the Profitability v2 tab. Model (owner, 2026-10-12):
// Net profit = Revenue − reserve − every expense (whole Azure RG + other costs), then Blue Dev / Owl.
// Missing figures render "—", never $0.00 (fail-visible).

import Box from '@mui/material/Box'
import SummaryLines from '../../../components/ui/SummaryLines'
import AppButton from '../../../components/ui/AppButton'
import { DataGrid, type DataGridColumn } from '../../../components/DataGrid'
import StatusChip from '../../../components/ui/StatusChip'
import type { StatusTone } from '../../../api/status'
import type {
  FinanceCustomerRow, FinanceExpenses, FinanceMonthRow, FinancePnl, FinanceResourceRow, FinanceBucket, FinanceSummary,
} from '../../../api/finance'

export const money = (n: number | null | undefined) =>
  n === null || n === undefined ? '—' : `${n < 0 ? '−' : ''}$${Math.abs(n).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`

const tone = (pct: number): StatusTone => (pct >= 40 ? 'green' : pct >= 20 ? 'amber' : 'red')
const signed = (n: number) => <Box component="span" sx={{ color: n < 0 ? 'error.main' : undefined }}>{money(n)}</Box>

// ── the month's statement ────────────────────────────────────────────────────────────────────────────

/** GitHub's expense: the amount when the feed has reported this month, else "—" + why (never a fake $0). */
const githubValue = (e: FinanceExpenses, available: boolean) => available ? money(e.github_usd) : '—'
const githubLabel = (available: boolean) => available ? 'GitHub (net, after included usage)' : 'GitHub — not connected yet (Data sources)'

export function PnlStatement({ pnl, expenses, settings, githubAvailable }: {
  pnl: FinancePnl; expenses: FinanceExpenses; settings: FinanceSummary['settings']; githubAvailable: boolean
}) {
  const owlPct = 100 - settings.dev_split_pct
  const extra = pnl.other_income_usd !== 0 || pnl.credits_usd !== 0
  if (pnl.model === 'bucket') return <BucketStatement pnl={pnl} expenses={expenses} githubAvailable={githubAvailable} extra={extra} />
  return (
    <SummaryLines maxWidth={620} lines={[
      ...(extra ? [
        { key: 'lic', label: `Licenses (${pnl.customers_licensed} customer${pnl.customers_licensed === 1 ? '' : 's'})`, value: money(pnl.license_usd) },
        { key: 'inc', label: '+ Other income', value: money(pnl.other_income_usd) },
        { key: 'cred', label: '− Credits / discounts', value: money(pnl.credits_usd) },
      ] : []),
      { key: 'rev', label: extra ? '= Revenue' : `Revenue (${pnl.customers_licensed} licensed customer${pnl.customers_licensed === 1 ? '' : 's'})`, value: money(pnl.revenue_usd), strong: true },
      { key: 'res', label: `− S·M·L·E reserve (${settings.reserve_pct}% of revenue)`, value: money(pnl.reserve_usd) },
      { key: 'az', label: `− Azure: customer DBs ${money(expenses.customer_db_usd)} · shared ${money(expenses.azure_shared_usd)} · unlicensed DBs ${money(expenses.unlicensed_db_usd)}`, value: money(expenses.azure_usd) },
      { key: 'oth', label: '− Other costs', value: money(expenses.other_usd) },
      { key: 'gh', label: `− ${githubLabel(githubAvailable)}`, value: githubValue(expenses, githubAvailable), muted: !githubAvailable },
      { key: 'net', label: `= Net profit (${pnl.net_margin_pct}% of revenue)`, value: signed(pnl.net_profit_usd), strong: true },
      { key: 'blue', label: `Blue Dev ${settings.dev_split_pct}%`, value: signed(pnl.blue_dev_usd) },
      { key: 'owl', label: `Owl ${owlPct}%`, value: signed(pnl.owl_usd) },
    ]} />
  )
}

/** D-COST-7 statement: the 40% operating bucket pays running costs first, then S·M·L·E; the rest is held. */
function BucketStatement({ pnl, expenses, githubAvailable, extra }: {
  pnl: FinancePnl; expenses: FinanceExpenses; githubAvailable: boolean; extra: boolean
}) {
  const bucketPct = pnl.bucket_pct ?? 0
  const targetPct = bucketPct - pnl.reserve_pct
  const costPct = pnl.revenue_usd > 0 ? Math.round(expenses.total_usd / pnl.revenue_usd * 1000) / 10 : null
  const onTarget = expenses.total_usd <= pnl.cost_target_usd
  return (
    <SummaryLines maxWidth={660} lines={[
      ...(extra ? [
        { key: 'lic', label: `Licenses (${pnl.customers_licensed} customer${pnl.customers_licensed === 1 ? '' : 's'})`, value: money(pnl.license_usd) },
        { key: 'inc', label: '+ Other income', value: money(pnl.other_income_usd) },
        { key: 'cred', label: '− Credits / discounts', value: money(pnl.credits_usd) },
      ] : []),
      { key: 'rev', label: extra ? '= Revenue' : `Revenue (${pnl.customers_licensed} licensed customer${pnl.customers_licensed === 1 ? '' : 's'})`, value: money(pnl.revenue_usd), strong: true },
      { key: 'bucket', label: `Operating bucket (${bucketPct}% of revenue) — pays running costs first, then S·M·L·E`, value: money(pnl.bucket_usd), strong: true },
      { key: 'az', label: `· Azure: customer DBs ${money(expenses.customer_db_usd)} · shared ${money(expenses.azure_shared_usd)} · unlicensed DBs ${money(expenses.unlicensed_db_usd)}`, value: money(expenses.azure_usd) },
      { key: 'gh', label: `· ${githubLabel(githubAvailable)}`, value: githubValue(expenses, githubAvailable), muted: !githubAvailable },
      { key: 'oth', label: '· Other costs', value: money(expenses.other_usd) },
      {
        key: 'run', label: <>= Running costs{costPct !== null ? ` (${costPct}% of revenue)` : ''} vs target {targetPct}% ({money(pnl.cost_target_usd)}){' '}
          <StatusChip status="target" tone={onTarget ? 'green' : 'amber'} label={onTarget ? 'on target' : `${money(expenses.total_usd - pnl.cost_target_usd)} over`} /></>,
        value: money(expenses.total_usd),
      },
      { key: 'smle', label: `· S·M·L·E (up to ${pnl.reserve_pct}%, after running costs)`, value: money(pnl.reserve_usd) },
      { key: 'held', label: '· Held — bucket left over, split later when released (Partners)', value: money(pnl.held_usd) },
      ...(pnl.overrun_usd > 0 ? [{ key: 'over', label: '− Running costs beyond the whole bucket (the partners carry this)', value: signed(-pnl.overrun_usd) }] : []),
      { key: 'net', label: `= Partners' share (${100 - bucketPct}% of revenue${pnl.overrun_usd > 0 ? ' − overrun' : ''})`, value: signed(pnl.net_profit_usd), strong: true },
      { key: 'blue', label: `Blue Dev ${pnl.dev_split_pct}%`, value: signed(pnl.blue_dev_usd) },
      { key: 'owl', label: `Owl ${100 - pnl.dev_split_pct}%`, value: signed(pnl.owl_usd) },
    ]} />
  )
}

export function ExpensesBlock({ expenses, githubAvailable }: { expenses: FinanceExpenses; githubAvailable: boolean }) {
  return (
    <SummaryLines lines={[
      { key: 'db', label: 'Azure — licensed customers’ own DBs', value: money(expenses.customer_db_usd) },
      { key: 'shared', label: 'Azure — shared (Function App, web app, storage, logs, shared DB…)', value: money(expenses.azure_shared_usd) },
      { key: 'unl', label: 'Azure — unlicensed / retired tenant DBs', value: money(expenses.unlicensed_db_usd) },
      { key: 'other', label: 'Other costs (below)', value: money(expenses.other_usd) },
      { key: 'gh', label: githubLabel(githubAvailable), value: githubValue(expenses, githubAvailable), muted: !githubAvailable },
      { key: 'total', label: 'Total expenses', value: money(expenses.total_usd), strong: true },
    ]} />
  )
}

// ── every month, one row each ────────────────────────────────────────────────────────────────────────

/** How a month's state reads in the table. "snapshot" marks a month whose Azure figures came from the hourly
 *  snapshot (before the cost export covered it) — it may read slightly low. */
export const STATUS: Record<FinanceMonthRow['status'], { label: string; color: string }> = {
  mtd: { label: 'month to date', color: 'primary.main' },
  open: { label: 'ended · closes soon', color: 'warning.main' },
  closed: { label: 'closed', color: 'text.secondary' },
  reopened: { label: 'reopened', color: 'warning.main' },
}

/** Shares are shown in $ — the % split can change from a month on (Settings), so a fixed % header would mislead. */
export function MonthlyPnlTable({ rows, onOpen }: { rows: FinanceMonthRow[]; onOpen: (month: string) => void }) {
  const cols: DataGridColumn<FinanceMonthRow>[] = [
    {
      key: 'month', header: 'Month', minWidth: 120, value: r => r.month,
      render: r => (
        <Box><b>{r.month}</b>
          <Box sx={{ fontSize: 11, color: STATUS[r.status].color }}>
            {STATUS[r.status].label}{r.cost_source === 'snapshot' && r.status !== 'mtd' ? ' · snapshot' : ''}
          </Box>
        </Box>
      ),
    },
    { key: 'lic', header: 'Licenses', align: 'right', hidden: true, value: r => r.license_usd, render: r => money(r.license_usd) },
    { key: 'inc', header: 'Other income', align: 'right', hidden: true, value: r => r.other_income_usd, render: r => money(r.other_income_usd) },
    { key: 'cred', header: 'Credits', align: 'right', hidden: true, value: r => r.credits_usd, render: r => money(r.credits_usd) },
    { key: 'rev', header: 'Revenue', align: 'right', value: r => r.revenue_usd, render: r => money(r.revenue_usd) },
    { key: 'res', header: 'Reserve', align: 'right', value: r => r.reserve_usd, render: r => money(r.reserve_usd) },
    { key: 'az', header: 'Azure', align: 'right', hidden: true, value: r => r.azure_usd, render: r => money(r.azure_usd) },
    { key: 'gh', header: 'GitHub', align: 'right', hidden: true, value: r => r.github_usd, render: r => money(r.github_usd) },
    { key: 'oth', header: 'Other costs', align: 'right', hidden: true, value: r => r.other_usd, render: r => money(r.other_usd) },
    { key: 'exp', header: 'Expenses', align: 'right', value: r => r.expenses_usd, render: r => <b>{money(r.expenses_usd)}</b> },
    { key: 'held', header: 'Held', align: 'right', value: r => r.held_usd, render: r => (r.model === 'bucket' ? money(r.held_usd) : '—') },
    { key: 'net', header: 'Net profit', align: 'right', value: r => r.net_profit_usd, render: r => <b>{signed(r.net_profit_usd)}</b> },
    { key: 'blue', header: 'Blue Dev', align: 'right', value: r => r.blue_dev_usd, render: r => signed(r.blue_dev_usd) },
    { key: 'owl', header: 'Owl', align: 'right', value: r => r.owl_usd, render: r => signed(r.owl_usd) },
    {
      key: 'margin', header: 'Net %', align: 'right', value: r => r.net_margin_pct, noFilter: true,
      render: r => <StatusChip status="pct" tone={tone(r.net_margin_pct)} label={`${r.net_margin_pct}%`} />,
    },
    {
      key: 'open', header: '', noFilter: true, width: 120,
      render: r => <AppButton size="small" aria-label={`Breakdown of ${r.month}`} onClick={() => onOpen(r.month)}>Breakdown →</AppButton>,
    },
  ]
  return <DataGrid rows={rows} columns={cols} storageKey="finance-v2-monthly-pnl-v2" getRowKey={r => r.month} dense />
}

// ── per customer ─────────────────────────────────────────────────────────────────────────────────────

export function CustomerTable({ rows, reservePct, onPrice }: { rows: FinanceCustomerRow[]; reservePct: number; onPrice: (pharmacyId: string) => void }) {
  const cols: DataGridColumn<FinanceCustomerRow>[] = [
    { key: 'name', header: 'Customer', minWidth: 200, width: 240, render: r => <b>{r.name}</b>, value: r => r.name },
    { key: 'license', header: 'License / mo', align: 'right', value: r => r.monthly_license_usd, render: r => money(r.monthly_license_usd) },
    {
      key: 'income', header: 'Income − credits', align: 'right', value: r => r.other_income_usd,
      render: r => r.other_income_usd === 0 ? '—' : <Box component="span" sx={{ color: r.other_income_usd < 0 ? 'error.main' : undefined }}>{money(r.other_income_usd)}</Box>,
    },
    { key: 'reserve', header: `Reserve ${reservePct}%`, align: 'right', value: r => r.reserve_usd, render: r => money(r.reserve_usd) },
    { key: 'db', header: 'Own DB cost', align: 'right', value: r => r.db_cost_usd, render: r => money(r.db_cost_usd) },
    { key: 'contrib', header: 'Contribution', align: 'right', value: r => r.contribution_usd, render: r => <b>{money(r.contribution_usd)}</b> },
    {
      key: 'pct', header: 'Contribution %', align: 'right', value: r => r.contribution_pct, noFilter: true,
      render: r => r.ended ? <StatusChip status="ended" tone="red" label="billing stopped" />
        : !r.licensed ? <StatusChip status="none" tone="grey" label="no license" />
        : r.contribution_pct === null ? <StatusChip status="none" tone="grey" label="no DB cost" />
        : <StatusChip status="pct" tone={tone(r.contribution_pct)} label={`${r.contribution_pct}%`} />,
    },
    {
      key: 'price', header: '', noFilter: true, width: 110,
      render: r => <AppButton size="small" aria-label={`Price for ${r.name}`} onClick={() => onPrice(r.pharmacy_id)}>{r.licensed || r.ended ? 'Price…' : 'Set price…'}</AppButton>,
    },
  ]
  return <DataGrid rows={rows} columns={cols} storageKey="finance-v2-customers-contribution" getRowKey={r => r.pharmacy_id} dense />
}

// ── Azure resources ──────────────────────────────────────────────────────────────────────────────────

const bucketChip = (b: FinanceBucket) =>
  b === 'customer' ? <StatusChip status="customer" tone="blue" label="Customer DB" />
    : b === 'unlicensed' ? <StatusChip status="unlicensed" tone="amber" label="Unlicensed DB" />
    : <StatusChip status="shared" tone="grey" label="Shared" />

const resourceCols: DataGridColumn<FinanceResourceRow>[] = [
  { key: 'resource', header: 'Resource', minWidth: 240, value: r => r.resource },
  { key: 'kind', header: 'Type', value: r => r.kind },
  { key: 'bucket', header: 'Bucket', value: r => r.bucket, render: r => bucketChip(r.bucket) },
  { key: 'customer', header: 'Customer', value: r => r.customer_name ?? '', render: r => r.customer_name ?? '—' },
  { key: 'cost', header: 'Cost', align: 'right', value: r => r.cost_usd, render: r => <b>{money(r.cost_usd)}</b> },
  { key: 'prior', header: 'Prior month', align: 'right', value: r => r.prior_usd, render: r => money(r.prior_usd) },
  {
    key: 'change', header: 'Δ prior', align: 'right', noFilter: true,
    value: r => (r.prior_usd === null ? null : r.cost_usd - r.prior_usd),
    render: r => r.prior_usd === null ? '—' : r.cost_usd === r.prior_usd ? <Box component="span" sx={{ color: 'text.secondary' }}>no change</Box> : (
      <Box component="span" sx={{ color: r.cost_usd > r.prior_usd ? 'error.main' : 'success.main' }}>
        {r.cost_usd > r.prior_usd ? '▲' : '▼'} {money(Math.abs(r.cost_usd - r.prior_usd))}
      </Box>
    ),
  },
]

type Meter = { resource: string; meter: string; cost_usd: number }

/** Azure resources; with the cost export (v2.3) each row also names what it was billed for. */
export function ResourceExplorer({ rows, meters }: { rows: FinanceResourceRow[]; meters: Meter[] }) {
  const byResource = new Map<string, Meter[]>()
  for (const m of meters) byResource.set(m.resource, [...(byResource.get(m.resource) ?? []), m])
  const cols: DataGridColumn<FinanceResourceRow>[] = meters.length === 0 ? resourceCols : [
    ...resourceCols.slice(0, 4),
    {
      key: 'meters', header: 'Billed for (top meters)', minWidth: 220, noFilter: true,
      value: r => (byResource.get(r.resource) ?? []).map(m => m.meter).join(', '),
      render: r => (
        <Box sx={{ fontSize: 12, color: 'text.secondary' }}>
          {(byResource.get(r.resource) ?? []).map(m => <Box key={m.meter}>{m.meter} · {money(m.cost_usd)}</Box>)}
        </Box>
      ),
    },
    ...resourceCols.slice(4),
  ]
  return <DataGrid rows={rows} columns={cols} storageKey="finance-v2-resources" getRowKey={r => r.resource} dense />
}
